import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CircleLoader } from "react-spinners";
import { TailSpin } from "react-loader-spinner";
import { LuSend } from "react-icons/lu";
import Icon from "../../assets/icons";
import useRequest from "../../components/hooks/use-request";
import { showToast } from "../../components/toast";
import RichTextEditor from "./rich-text-editor";
import EmailPreviewPane from "./email-preview";
import AudienceFilterPanel from "./audience-filter";
import ConfirmModal from "./confirm-modal";
import CampaignActions from "./campaign-actions";
import StatusPill from "./status-pill";
import {
  AudienceFilter,
  CAMPAIGN_TEMPLATE_CATEGORIES,
  EmailCampaign,
  EmailPreview,
  EmailTemplate,
  KNOWN_CATEGORIES,
  toMessage,
} from "./api";

const inputCls =
  "w-full h-[42px] px-3 border border-[#0979A1] rounded-lg text-[14px] outline-none focus:border-[#0979A1] disabled:bg-gray-50 disabled:border-gray-200";
const labelCls = "text-[13px] font-medium text-[#040821] mb-1.5 block";

const asList = (data: any, key: string): any[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.[key])) return data[key];
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const CampaignEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === "new";

  const [campaign, setCampaign] = useState<EmailCampaign | null>(null);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("newsletter");
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [audience, setAudience] = useState<AudienceFilter>({});
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [loaded, setLoaded] = useState(isNew);
  const [testEmail, setTestEmail] = useState("");
  const [testOpen, setTestOpen] = useState(false);

  const { makeRequest: getCampaign, loading: loadingCampaign } = useRequest(
    `/email-campaigns/${id}`,
    "GET"
  );
  const { makeRequest: createCampaign, loading: creating } = useRequest(
    "/email-campaigns",
    "POST"
  );
  const { makeRequest: updateCampaign, loading: updating } = useRequest(
    `/email-campaigns/${id}`,
    "PATCH"
  );
  const { makeRequest: getPreview, loading: loadingPreview } = useRequest(
    `/email-campaigns/${id}/preview`,
    "GET"
  );
  const { makeRequest: getTemplates } = useRequest("/email-templates", "GET");
  const { makeRequest: testSend, loading: sendingTest } = useRequest(
    `/email-campaigns/${id}/test-send`,
    "POST"
  );

  const saving = creating || updating;
  // Only a draft can be edited — PATCH returns 400 for any other status
  const editable = isNew || campaign?.status === "draft";

  const categoryOptions = useMemo(() => {
    const opts = [...KNOWN_CATEGORIES] as string[];
    if (campaign?.category && !opts.includes(campaign.category))
      opts.push(campaign.category);
    return opts;
  }, [campaign]);

  // A campaign may only be built from approved newsletter/announcement
  // templates — onboarding templates belong to the transactional flow.
  const selectableTemplates = useMemo(
    () =>
      templates.filter(
        (t) =>
          t.status === "approved" &&
          CAMPAIGN_TEMPLATE_CATEGORIES.includes(
            (t.category || "").toLowerCase()
          )
      ),
    [templates]
  );

  const applyCampaign = (c: EmailCampaign) => {
    setCampaign(c);
    setName(c.name || "");
    setCategory(c.category || "newsletter");
    setTemplateId(c.templateId || "");
    setSubject(c.subject || "");
    setBodyHtml(c.bodyHtml || "");
    setAudience(c.audienceFilter || {});
  };

  const loadPreview = async () => {
    if (isNew) return;
    const [res] = await getPreview();
    if (res?.data) setPreview(res.data);
  };

  useEffect(() => {
    const load = async () => {
      const [tRes] = await getTemplates();
      if (tRes?.data !== undefined)
        setTemplates(asList(tRes.data, "templates"));
      if (isNew) return;
      const [res] = await getCampaign();
      if (res?.data) applyCampaign(res.data);
      setLoaded(true);
      loadPreview();
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Choosing a template seeds the campaign's own subject/body, which stay
  // editable from here — the campaign can diverge from the template.
  const handleTemplateChange = (value: string) => {
    setTemplateId(value);
    const t = selectableTemplates.find((x) => x.id === value);
    if (t) {
      setSubject(t.subject || "");
      setBodyHtml(t.bodyHtml || "");
    }
  };

  const validate = (): string | null => {
    if (!name.trim()) return "Campaign name is required";
    if (!category) return "Category is required";
    if (!templateId && !bodyHtml.trim())
      return "Pick a template or write the email body";
    if (!templateId && !subject.trim())
      return "Subject is required when not using a template";
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) return showToast(err, false, { position: "top-center" });

    const payload: Record<string, any> = {
      name: name.trim(),
      category: category,
      subject: subject.trim() || undefined,
      bodyHtml: bodyHtml || undefined,
      audienceFilter: Object.keys(audience).length ? audience : undefined,
    };
    if (templateId) payload.templateId = templateId;

    if (isNew) {
      const [res, code] = await createCampaign(payload);
      if (code >= 200 && code < 300 && res?.data?.id) {
        showToast(toMessage(res, "Campaign created"), true, {
          position: "top-center",
        });
        navigate(`/email-management/campaigns/${res.data.id}`, {
          replace: true,
        });
      } else {
        showToast(toMessage(res, "Failed to create campaign"), false, {
          position: "top-center",
        });
      }
      return;
    }

    const [res, code] = await updateCampaign(payload);
    if (code >= 200 && code < 300) {
      showToast(toMessage(res, "Campaign saved"), true, {
        position: "top-center",
      });
      // HTML is sanitised server-side on save; re-read so what is shown is
      // what was stored, then refresh the preview.
      const [fresh] = await getCampaign();
      if (fresh?.data) applyCampaign(fresh.data);
      loadPreview();
    } else {
      showToast(toMessage(res, "Failed to save campaign"), false, {
        position: "top-center",
      });
    }
  };

  const handleTestSend = async () => {
    const [res, code] = await testSend({ email: testEmail.trim() });
    if (code >= 200 && code < 300) {
      showToast(toMessage(res, `Test email sent to ${testEmail}`), true, {
        position: "top-center",
      });
      setTestOpen(false);
    } else {
      showToast(toMessage(res, "Failed to send test email"), false, {
        position: "top-center",
      });
    }
  };

  const refetch = async () => {
    const [fresh] = await getCampaign();
    if (fresh?.data) applyCampaign(fresh.data);
    loadPreview();
  };

  if (!isNew && !loaded && loadingCampaign) {
    return (
      <div className="flex justify-center items-center h-96">
        <TailSpin color="skyblue" />
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-3 mb-6 w-[95%]">
        <button onClick={() => navigate(-1)} className="cursor-pointer">
          <Icon name="arrowLeft" />
        </button>
        <h2 className="text-[22px] font-bold">
          {isNew ? "New Campaign" : "Campaign"}
        </h2>
        {campaign?.status && <StatusPill value={campaign.status} />}
      </div>

      {!isNew && !editable && (
        <div className="w-[95%] mb-4 border border-gray-200 bg-gray-50 rounded-lg px-4 py-3">
          <p className="text-[12px] text-gray-600">
            This campaign is no longer a draft, so its content is read-only.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 w-[95%] items-start">
        {/* Left — content + audience */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-[16px] font-semibold mb-5">Campaign Details</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={labelCls}>Campaign Name</label>
                <input
                  value={name}
                  disabled={!editable}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. October Newsletter"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Category</label>
                <select
                  value={category}
                  disabled={!editable}
                  onChange={(e) => setCategory(e.target.value)}
                  className={`${inputCls} cursor-pointer`}>
                  {categoryOptions.map((c) => (
                    <option key={c} value={c} className="capitalize">
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label className={labelCls}>Template</label>
              <select
                value={templateId}
                disabled={!editable}
                onChange={(e) => handleTemplateChange(e.target.value)}
                className={`${inputCls} cursor-pointer`}>
                <option value="">No template — write from scratch</option>
                {selectableTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.category})
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-gray-400 mt-1">
                Only approved newsletter and announcement templates can be used.
                Edits below apply to this campaign only.
              </p>
            </div>

            <div className="mb-4">
              <label className={labelCls}>Subject</label>
              <input
                value={subject}
                disabled={!editable}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. Hi {{first_name}}, what's new this month"
                className={inputCls}
              />
            </div>

            <label className={labelCls}>Email Body</label>
            <RichTextEditor
              value={bodyHtml}
              onChange={setBodyHtml}
              readOnly={!editable}
            />

            {editable && (
              <div className="flex flex-wrap items-center gap-3 mt-6">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-[#0979A1] text-white rounded-lg px-5 py-2.5 text-[14px] font-semibold hover:bg-[#076a8a] disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2">
                  {saving ? (
                    <CircleLoader color="#ffffff" loading size={18} />
                  ) : isNew ? (
                    "Create Campaign"
                  ) : (
                    "Save Draft"
                  )}
                </button>
                <button
                  onClick={() =>
                    navigate("/email-management", {
                      state: { tab: "Campaigns" },
                    })
                  }
                  className="px-5 py-2.5 border border-gray-200 rounded-lg text-[14px] hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            )}
          </div>

          <AudienceFilterPanel
            value={audience}
            onChange={setAudience}
            disabled={!editable}
            campaignId={isNew ? undefined : id}
          />
        </div>

        {/* Right — workflow, preview, test send */}
        <div className="space-y-6">
          {!isNew && campaign && (
            <CampaignActions campaign={campaign} onChanged={refetch} />
          )}

          <EmailPreviewPane
            preview={preview}
            loading={loadingPreview}
            onRefresh={isNew ? undefined : loadPreview}
            emptyLabel={
              isNew
                ? "Create the campaign to generate a preview."
                : "No preview available yet."
            }
          />

          {!isNew && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
              <h3 className="text-[16px] font-semibold mb-1">Send a test</h3>
              <p className="text-[13px] text-gray-500 mb-4">
                Sends one real email to the address below, using the saved
                version.
              </p>
              <div className="flex flex-wrap gap-3">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="name@example.com"
                  className={`${inputCls} flex-1 min-w-[220px]`}
                />
                <button
                  onClick={() => setTestOpen(true)}
                  disabled={!testEmail.trim() || sendingTest}
                  className="flex items-center gap-2 px-5 py-2.5 border border-[#0979A1] text-[#0979A1] rounded-lg text-[14px] font-semibold hover:bg-[#E8F7FA] disabled:opacity-50 disabled:cursor-not-allowed">
                  <LuSend size={15} /> Send test
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        visible={testOpen}
        title="Send test email"
        message={
          <>
            Send a real test email to <strong>{testEmail}</strong>? It uses the
            last saved version of this campaign.
          </>
        }
        confirmLabel="Send test"
        loading={sendingTest}
        onConfirm={handleTestSend}
        onClose={() => setTestOpen(false)}
      />
    </>
  );
};

export default CampaignEditor;
